import type { ReactNode } from 'react';
import { FeatureFlag } from '@metycle/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFeatureFlag } from '@/lib/feature-flags';
import type { AllocateFillFormState } from '../lib/allocate-fill-state';
import { FxRefPicker } from './fx-ref-picker';
import { MetalRefPicker } from './metal-ref-picker';

export interface ContractReferenceFieldProps {
  isFx: boolean;
  state: AllocateFillFormState;
  set: (key: keyof AllocateFillFormState, value: string) => void;
}

function ReferenceShell({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <div className="flex flex-col gap-1">
      <Label>Contract reference</Label>
      {children}
    </div>
  );
}

// References must be a real StoneX trade id, so pick from the broker feed
// instead of typing — FX narrowed to the selected pair, metals (behind the
// metal-trade-ref-picker flag) to the selected metal. Off keeps the metal
// free-text field exactly as today.
export function ContractReferenceField({
  isFx,
  state,
  set,
}: Readonly<ContractReferenceFieldProps>) {
  const metalPickerOn = useFeatureFlag(FeatureFlag.METAL_TRADE_REF_PICKER, false);
  const value = state.contractReference === '' ? null : state.contractReference;
  const onPick = (tradeId: string | null) => {
    set('contractReference', tradeId ?? '');
  };
  if (isFx) {
    return (
      <ReferenceShell>
        <FxRefPicker
          value={value}
          currencyPair={state.currencyPair !== '' ? state.currencyPair : undefined}
          onChange={onPick}
          aria-label="Contract reference"
        />
      </ReferenceShell>
    );
  }
  if (metalPickerOn) {
    return (
      <ReferenceShell>
        {/* Free text stays allowed: the broker feed refreshes on sync, so a
            fill executed minutes ago may not be listed yet. */}
        <MetalRefPicker
          value={value}
          metal={state.metal !== '' ? state.metal : undefined}
          onChange={onPick}
          allowFreeText
          aria-label="Contract reference"
        />
      </ReferenceShell>
    );
  }
  return (
    <ReferenceShell>
      <Input
        value={state.contractReference}
        placeholder="e.g. LME-CU-MAY26"
        onChange={(event) => {
          set('contractReference', event.target.value);
        }}
      />
    </ReferenceShell>
  );
}
